/**
 * API Service Layer
 * Handles all API communication with proper error handling and interceptors
 */
import config from '../config';

class ApiService {
  constructor() {
    this.baseUrl = config.api.baseUrl;
    this.timeout = config.api.timeout;
  }

  /**
   * Generic fetch wrapper with error handling and automatic token refresh
   */
  async request(endpoint, options = {}, isRetry = false) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      credentials: 'include', // Include cookies in requests
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...(options?.headers ?? {}),
      },
    };

    // Special handling for FormData - let browser set Content-Type with boundary
    if (options.body instanceof FormData) {
      delete mergedOptions.headers['Content-Type'];
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...mergedOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle 401 responses with automatic token refresh
      // Skip token refresh for user activities to avoid cascading errors for non-authenticated users
      if (response.status === 401 && !isRetry && endpoint !== '/auth/refresh' && endpoint !== '/auth/logout' && endpoint !== '/api/user-activities') {
        try {
          const refreshResponse = await this.refreshToken();
          if (refreshResponse.ok) {
            // Retry the original request with the new token
            return this.request(endpoint, options, true);
          }
        } catch (refreshError) {
          // Let the original 401 error propagate
        }
      }

      if (!response.ok) {
        // Create a more informative error for auth endpoints
        const errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.endpoint = endpoint;
        throw error;
      }

      return response;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * Attempt to refresh the access token using the refresh token
   */
  async refreshToken() {
    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  /**
   * Get authentication headers
   * @deprecated Authentication now uses httpOnly cookies sent automatically
   */
  getAuthHeaders() {
    // With cookie-based auth, no headers needed - cookies are sent automatically
    return {};
  }

  // Convenience methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    // Don't stringify FormData - let browser handle it
    const body = data instanceof FormData ? data : JSON.stringify(data);
    
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body,
    });
  }

  async put(endpoint, data, options = {}) {
    // Don't stringify FormData - let browser handle it
    const body = data instanceof FormData ? data : JSON.stringify(data);
    
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body,
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Submit easter egg to unlock achievement
   */
  async submitEasterEgg(easterEgg) {
    return this.post('/api/achievements/easter-egg', { easterEgg });
  }

  /**
   * Update user bio
   */
  async updateBio(userId, bio) {
    return this.put(`/auth/users/${userId}/bio`, { bio });
  }

  /**
   * Get full URL for external navigation (OAuth, file downloads, etc.)
   */
  getFullUrl(endpoint) {
    return `${this.baseUrl}${endpoint}`;
  }
}

export default new ApiService();